/**
 * SystemAnalyticsPage.js
 * Workspace summary dashboard — projects, Q.Banks, Q.Sets, evaluations.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExamPageShell from '../components/layout/ExamPageShell';
import EmptyState from '../components/common/EmptyState';
import { listExamProjects, listEvaluations } from '../api/examApi';

const hasQBank = (p) =>
  p.project_type === 'qbank_upload' ||
  (p.qbank_status && p.qbank_status !== 'pending');

// ── Mini progress bar ─────────────────────────────────────────────────────────
function ProgressBar({ done, total, color }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--exam-border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || 'var(--exam-primary)', borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--exam-text-secondary)', whiteSpace: 'nowrap' }}>
        {done}/{total}
      </span>
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, sub }) {
  return (
    <div className="exam-card" style={{ padding: '14px 20px', textAlign: 'center', minWidth: 130, flex: '1 1 130px' }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || 'var(--exam-primary)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--exam-text)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--exam-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Project row ───────────────────────────────────────────────────────────────
function ProjectRow({ project: p, evaluations }) {
  const [expanded, setExpanded] = useState(false);
  const totalStudents    = evaluations.reduce((s, e) => s + (e.student_count   || 0), 0);
  const evaluatedStudents = evaluations.reduce((s, e) => s + (e.evaluated_count || 0), 0);
  const activeEvals = evaluations.filter((e) => ['pending','evaluating','faculty_review'].includes(e.status)).length;

  return (
    <div className="exam-card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header row */}
      <div
        onClick={() => evaluations.length > 0 && setExpanded((v) => !v)}
        style={{
          padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
          cursor: evaluations.length > 0 ? 'pointer' : 'default',
        }}
      >
        {/* Project name + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--exam-text)' }}>{p.title}</span>
            {hasQBank(p) && (
              <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'var(--exam-primary-light)', color: 'var(--exam-primary)', fontWeight: 600 }}>
                Q.Bank
              </span>
            )}
          </div>
          <span style={{ fontSize: 11, color: 'var(--exam-text-muted)' }}>
            {p.course_name || p.institution_name || '—'}
            {p.course_code ? ` · ${p.course_code}` : ''}
          </span>
        </div>

        {/* Counters */}
        <div style={{ display: 'flex', gap: 20, flexShrink: 0, alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--exam-primary)' }}>{p.question_sets_count || 0}</div>
            <div style={{ fontSize: 10, color: 'var(--exam-text-muted)' }}>Q.Sets</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: evaluations.length ? 'var(--exam-text)' : 'var(--exam-text-muted)' }}>{evaluations.length}</div>
            <div style={{ fontSize: 10, color: 'var(--exam-text-muted)' }}>Sessions</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: evaluatedStudents > 0 ? 'var(--exam-success)' : 'var(--exam-text-muted)' }}>{evaluatedStudents}</div>
            <div style={{ fontSize: 10, color: 'var(--exam-text-muted)' }}>Evaluated</div>
          </div>
          {activeEvals > 0 && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--exam-warning-light)', color: 'var(--exam-warning)', fontWeight: 600 }}>
              {activeEvals} active
            </span>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Link
            to={`/examination/projects/${p.id}/analytics`}
            className="exam-btn exam-btn-ghost exam-btn-sm"
            style={{ textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            Report →
          </Link>
          {evaluations.length > 0 && (
            <span style={{ fontSize: 14, color: 'var(--exam-text-muted)', lineHeight: '28px' }}>
              {expanded ? '▲' : '▼'}
            </span>
          )}
        </div>
      </div>

      {/* Expanded: per-session breakdown */}
      {expanded && evaluations.length > 0 && (
        <div style={{ borderTop: '1px solid var(--exam-border)', background: 'var(--exam-surface-alt)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--exam-border)' }}>
                {['Session', 'Status', 'Students', 'Evaluated', 'Progress'].map((h) => (
                  <th key={h} style={{ padding: '6px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--exam-text-secondary)', fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {evaluations.map((e) => {
                const done  = e.evaluated_count || 0;
                const total = e.student_count   || 0;
                const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
                const statusColor = {
                  completed: 'var(--exam-success)',
                  published: 'var(--exam-success)',
                  pending:   'var(--exam-warning)',
                  evaluating:'var(--exam-primary)',
                }[e.status] || 'var(--exam-text-muted)';
                return (
                  <tr key={e.id} style={{ borderTop: '1px solid var(--exam-border)' }}>
                    <td style={{ padding: '8px 16px', fontWeight: 600, color: 'var(--exam-text)' }}>
                      {e.title || `Session #${e.id}`}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: statusColor + '22', color: statusColor, fontWeight: 600, textTransform: 'capitalize' }}>
                        {e.status?.replace('_', ' ') || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 16px', color: 'var(--exam-text)' }}>{total || '—'}</td>
                    <td style={{ padding: '8px 16px', color: 'var(--exam-text)' }}>{done || '—'}</td>
                    <td style={{ padding: '8px 16px', minWidth: 140 }}>
                      {total > 0 ? (
                        <ProgressBar done={done} total={total} color={pct === 100 ? 'var(--exam-success)' : 'var(--exam-primary)'} />
                      ) : <span style={{ color: 'var(--exam-text-muted)' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SystemAnalyticsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    listExamProjects()
      .then(async (data) => {
        const projects = Array.isArray(data) ? data : (data.projects || []);
        // Sort newest first
        projects.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const results = await Promise.allSettled(
          projects.map((p) =>
            listEvaluations(p.id)
              .then((evals) => ({ project: p, evaluations: Array.isArray(evals) ? evals : [] }))
              .catch(() => ({ project: p, evaluations: [] }))
          )
        );
        setRows(results.map((r) => (r.status === 'fulfilled' ? r.value : null)).filter(Boolean));
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter(({ project: p }) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.title?.toLowerCase().includes(q) || p.course_name?.toLowerCase().includes(q);
  });

  // Summary stats
  const totalProjects     = rows.length;
  const totalQBanks       = rows.filter(({ project: p }) => hasQBank(p)).length;
  const totalQSets        = rows.reduce((s, { project: p }) => s + (p.question_sets_count || 0), 0);
  const totalEvaluated    = rows.reduce((s, { evaluations: e }) => s + e.reduce((a, ev) => a + (ev.evaluated_count || 0), 0), 0);
  const totalStudents     = rows.reduce((s, { evaluations: e }) => s + e.reduce((a, ev) => a + (ev.student_count || 0), 0), 0);

  return (
    <ExamPageShell breadcrumbs={[{ label: 'Analytics' }]}>
      <div style={{ maxWidth: 1040, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--exam-text)' }}>Analytics Overview</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--exam-text-muted)' }}>
            Workspace summary — projects, Q.Banks, question sets, and evaluation progress.
          </p>
        </div>

        {/* Summary stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
          <StatCard label="Projects"          value={totalProjects}  color="var(--exam-primary)" />
          <StatCard label="Q.Banks"           value={totalQBanks}    color="var(--exam-primary-dark, var(--exam-primary))" />
          <StatCard label="Question Sets"     value={totalQSets}     color="var(--exam-text)" />
          <StatCard
            label="Students Evaluated"
            value={totalEvaluated}
            color={totalEvaluated > 0 ? 'var(--exam-success)' : 'var(--exam-text-muted)'}
            sub={totalStudents > 0 ? `of ${totalStudents} enrolled` : undefined}
          />
        </div>

        {/* Search */}
        <div style={{ marginBottom: 16 }}>
          <input
            className="exam-input"
            placeholder="Search projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 340 }}
          />
        </div>

        {/* Column headers */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: 'flex', padding: '4px 18px 6px', fontSize: 11, fontWeight: 600, color: 'var(--exam-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', gap: 14 }}>
            <span style={{ flex: 1 }}>Project</span>
            <span style={{ width: 56, textAlign: 'center' }}>Q.Sets</span>
            <span style={{ width: 64, textAlign: 'center' }}>Sessions</span>
            <span style={{ width: 74, textAlign: 'center' }}>Evaluated</span>
            <span style={{ width: 80 }} />
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="exam-spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="📊" title="No projects yet" description="Create a project to see analytics here." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(({ project, evaluations }) => (
              <ProjectRow key={project.id} project={project} evaluations={evaluations} />
            ))}
          </div>
        )}
      </div>
    </ExamPageShell>
  );
}
